import { useEffect, useState } from "react";

/**
 * Rewrite Convex storage URLs to go through the local /convex nginx alias so
 * the browser stays same-origin.
 *
 * - Self-hosted Convex returns relative paths like "/api/storage/<uuid>" —
 *   those must be prefixed with "/convex" or the browser would resolve them
 *   against the frontend origin (which doesn't host /api/storage/*) and 404.
 * - Cloud Convex returns absolute URLs against a separate hostname; those also
 *   get rewritten to the same /convex path so we never hit CORS.
 */
export function proxyStorageUrl(url: string): string {
  if (url.startsWith("/api/")) {
    return `/convex${url}`;
  }
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== window.location.hostname) {
      return `/convex${parsed.pathname}${parsed.search}`;
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * Fetch a file from a (proxied) storage URL and return a blob URL suitable for
 * use in <img> / <iframe>. Avoids redirect + CORS issues with Convex self-hosted
 * storage passing through nginx.
 *
 * Used by FilePreviewDrawer and DocumentCompareDrawer.
 */
export function useBlobUrl(storageUrl: string | null | undefined) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!storageUrl) {
      setBlobUrl(null);
      return;
    }
    let revoked = false;
    setLoading(true);
    setError(false);

    const proxied = proxyStorageUrl(storageUrl);
    fetch(proxied)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        setBlobUrl(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [storageUrl]);

  return { blobUrl, loading, error };
}
